import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "@/context/NotificationsContext";
import { useOrders } from "@/context/OrdersContext";
import type { BranchNotification, NotificationFilter, NotificationType } from "@/mocks/branch/notifications";

const TYPE_CONFIG: Record<NotificationType, { icon: string; accent: string; group: NotificationFilter }> = {
  new_order: { icon: "ri-shopping-bag-line", accent: "bg-primary-100 text-primary-700", group: "orders" },
  debt: { icon: "ri-alert-line", accent: "bg-accent-100 text-accent-900", group: "orders" },
  branch_status: { icon: "ri-store-2-line", accent: "bg-secondary-100 text-secondary-900", group: "system" },
  system: { icon: "ri-settings-3-line", accent: "bg-secondary-100 text-secondary-900", group: "system" },
  rating: { icon: "ri-star-line", accent: "bg-accent-100 text-accent-900", group: "ratings" },
};

const FILTERS: { key: NotificationFilter; label: string }[] = [
  { key: "all", label: "الكل" },
  { key: "unread", label: "غير المقروء" },
  { key: "orders", label: "الطلبات" },
  { key: "ratings", label: "التقييمات" },
  { key: "system", label: "النظامية" },
];

function matches(n: BranchNotification, filter: NotificationFilter): boolean {
  if (filter === "all") return true;
  if (filter === "unread") return !n.read;
  return TYPE_CONFIG[n.type].group === filter;
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const { openOrderByNumber } = useOrders();
  const [filter, setFilter] = useState<NotificationFilter>("all");

  const filtered = useMemo(() => notifications.filter((n) => matches(n, filter)), [notifications, filter]);

  const handleOpen = (n: BranchNotification) => {
    markRead(n.id);
    if (n.orderNumber) {
      openOrderByNumber(n.orderNumber);
      navigate("/orders");
    }
  };

  return (
    <div className="max-w-[900px] mx-auto space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-slide-up">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900">الإشعارات</h2>
          <p className="mt-1 text-sm text-foreground-500">
            {unreadCount > 0 ? `لديك ${unreadCount} إشعارات غير مقروءة` : "لا توجد إشعارات غير مقروءة"}
          </p>
        </div>
        {unreadCount > 0 ? (
          <button
            type="button"
            onClick={markAllRead}
            className="self-start sm:self-auto inline-flex items-center gap-2 px-4 h-10 rounded-btn bg-background-200 text-foreground-700 text-sm font-semibold hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-check-double-line text-lg"></i>
            تعليم الكل كمقروء
          </button>
        ) : null}
      </div>

      <div className="flex items-start gap-2.5 px-4 py-3 rounded-card border border-secondary-200 bg-secondary-50">
        <i className="ri-notification-3-line text-secondary-700 text-lg mt-0.5 shrink-0"></i>
        <p className="text-xs text-secondary-700 leading-relaxed">
          لافتة الطلب الجديد البصرية تبقى حتى فتح الطلب أو بدء تحضيره. التنبيه الصوتي معطّل حالياً
          بناءً على إعداد النظام.
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 px-1 py-1 bg-background-50 rounded-full border border-background-200 w-fit max-w-full overflow-x-auto">
        {FILTERS.map((tab) => {
          const active = filter === tab.key;
          const count = notifications.filter((n) => matches(n, tab.key)).length;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setFilter(tab.key)}
              className={`px-4 h-9 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                active ? "bg-primary-500 text-white" : "text-foreground-500 hover:text-foreground-800"
              }`}
            >
              {tab.label}
              {tab.key === "unread" && unreadCount > 0 ? <span className="mr-1.5 text-xs">({unreadCount})</span> : null}
              {tab.key !== "all" && tab.key !== "unread" && count > 0 ? (
                <span className={`mr-1.5 text-xs ${active ? "text-white/80" : "text-foreground-400"}`}>({count})</span>
              ) : null}
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-20 bg-background-50 rounded-card border border-background-200 animate-fade-slide-up">
          <div className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center">
            <i className="ri-notification-off-line text-3xl text-foreground-400"></i>
          </div>
          <h3 className="mt-4 text-lg font-bold text-foreground-900">لا توجد إشعارات</h3>
          <p className="mt-1.5 text-sm text-foreground-500">
            لا توجد إشعارات مطابقة لهذا الفلتر حالياً
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((n) => {
            const cfg = TYPE_CONFIG[n.type];
            return (
              <button
                key={n.id}
                type="button"
                onClick={() => handleOpen(n)}
                className={`w-full flex items-start gap-3.5 p-4 rounded-card border text-right transition-all cursor-pointer animate-fade-slide-up ${
                  n.read ? "bg-background-50 border-background-200" : "bg-primary-50/40 border-primary-200 hover:border-primary-300"
                }`}
              >
                <div className={`w-11 h-11 rounded-btn flex items-center justify-center shrink-0 ${cfg.accent}`}>
                  <i className={`${cfg.icon} text-xl`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-foreground-900">{n.title}</p>
                    <span className="text-xs text-foreground-500 whitespace-nowrap">{n.time}</span>
                  </div>
                  <p className="mt-1 text-sm text-foreground-500 leading-relaxed">{n.message}</p>
                  {n.orderNumber ? (
                    <span className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-primary-700">
                      <i className="ri-arrow-left-line"></i>
                      عرض الطلب #{n.orderNumber}
                    </span>
                  ) : null}
                </div>
                {!n.read ? <span className="w-2.5 h-2.5 rounded-full bg-primary-500 shrink-0 mt-1.5"></span> : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}